"use client"

import { TbChartPie, TbShieldCheck, TbCircleCheck, TbAlertTriangle } from "react-icons/tb"
import { CATEGORIES } from "./data"

export type CostDistributionProps = {
   workingCosts: Record<string, number>
   totalStartupCost: number
}

function getCategorySum(catKey: string, workingCosts: Record<string, number>) {
   return CATEGORIES[catKey].items.reduce((sum, item) => sum + (workingCosts[item.id] || 0), 0)
}

export default function CostDistribution({ workingCosts, totalStartupCost }: CostDistributionProps) {
   const spaceTotal = getCategorySum("space", workingCosts)
   const workingTotal = getCategorySum("working", workingCosts)
   const pctSpace = totalStartupCost ? (spaceTotal / totalStartupCost) * 100 : 0
   const pctWorking = totalStartupCost ? (workingTotal / totalStartupCost) * 100 : 0

   return (
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
         <div className="mb-7 flex items-center gap-3 text-custom-2xl-bold">
            <TbChartPie className="h-6 w-6 text-custom-primary shrink-0" />
            <span>Cost Distribution Allocation Summary</span>
         </div>

         {/* Bar Track */}
         <div className="mb-6 flex h-4 overflow-hidden rounded-full bg-slate-200">
            {Object.keys(CATEGORIES).map((catKey) => {
               const catSum = getCategorySum(catKey, workingCosts)
               const percentage = totalStartupCost ? (catSum / totalStartupCost) * 100 : 0
               return (
                  <div
                     key={catKey}
                     style={{
                        width: `${percentage}%`,
                        backgroundColor: CATEGORIES[catKey].color,
                     }}
                     className="h-full transition-all duration-300"
                  />
               )
            })}
         </div>

         {/* Distribution Legend Grid */}
         <div className="min-[550px]:grid-cols-2 grid grid-cols-1 gap-3 text-custom-sm-medium">
            {Object.keys(CATEGORIES).map((catKey) => {
               const catSum = getCategorySum(catKey, workingCosts)
               const percentage = totalStartupCost ? ((catSum / totalStartupCost) * 100).toFixed(0) : "0"
               return (
                  <div
                     key={catKey}
                     className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-custom-sm-medium"
                  >
                     <div className="flex min-w-0 items-center gap-2">
                        <span
                           className="h-2.5 w-2.5 shrink-0 rounded-full"
                           style={{ backgroundColor: CATEGORIES[catKey].color }}
                        />
                        <span>{CATEGORIES[catKey].label}</span>
                     </div>
                     <span className="shrink-0 text-custom-sm-medium">
                        {percentage}% (${catSum.toLocaleString()})
                     </span>
                  </div>
               )
            })}
         </div>

         {/* Guardrails */}
         <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-[#FAF9F6] p-5">
            <div className="mb-2.5 flex items-center gap-1.5 text-custom-xl-semibold">
               <TbShieldCheck className="h-5 w-5 text-custom-primary shrink-0" /> Standard Benchmark Guardrails
            </div>
            <div className="flex flex-col gap-2">
               {pctSpace >= 25 && pctSpace <= 45 ? (
                  <div className="flex items-start gap-2 rounded border-l-4 border-emerald-500 bg-emerald-50 p-2.5 text-custom-sm-medium text-emerald-800">
                     <TbCircleCheck className="mt-1 h-4 w-4 shrink-0" />
                     <span>Buildout Component ({pctSpace.toFixed(0)}%): Matches safe target metrics.</span>
                  </div>
               ) : pctSpace > 45 ? (
                  <div className="flex items-start gap-2 rounded border-l-4 border-amber-500 bg-amber-50 p-2.5 text-custom-sm-medium  text-amber-800">
                     <TbAlertTriangle className="mt-1 h-4 w-4 shrink-0" />
                     <span>Buildout Capital Alert ({pctSpace.toFixed(0)}%): Heavy capital lock.</span>
                  </div>
               ) : null}

               {pctWorking >= 15 ? (
                  <div className="flex items-start gap-2 rounded border-l-4 border-emerald-500 bg-emerald-50 p-2.5 text-custom-sm-medium text-emerald-800">
                     <TbCircleCheck className="mt-1 h-4 w-4 shrink-0" />
                     <span>Liquidity Runway ({pctWorking.toFixed(0)}%): Optimal preservation airbag rules.</span>
                  </div>
               ) : (
                  <div className="flex items-start gap-2 rounded border-l-4 border-amber-500 bg-amber-50 p-2.5 text-custom-sm-medium  text-amber-800">
                     <TbAlertTriangle className="mt-1 h-4 w-4 shrink-0" />
                     <span>Under-capitalization threat.</span>
                  </div>
               )}
            </div>
         </div>
      </div>
   )
}
